import { DEVICE_TYPES } from '/src/features/_global/constants';
import { globalFeatureKeys } from '/src/features/_global/queries';
import {
  DEFAULT_VISITS_VALUE,
  SNAPSHOT_BLOCKING_POPUP_OPTIONS,
  SNAPSHOT_END_OPTIONS,
  SNAPSHOT_START_OPTIONS,
  SNAPSHOT_TRACKING_OPTIONS,
} from '/src/features/snapshots/constants';
import { getFlowDestination, normalizeUrlWithHttps, withQueryParams } from './utils';

export async function completeOnboarding({
  mode = 'complete',
  completionUrl,
  isHeatmapFlow,
  flow,
  completedAt,
  createSite,
  createSnapshots,
  upsertUserOnboarding,
  navigate,
  notifications,
  queryClient,
}) {
  const isSkipMode = mode === 'skip';
  let createdHeatmapsCount = 0;

  try {
    if (completionUrl) {
      if (isHeatmapFlow) {
        const normalizedHeatmapUrl = normalizeUrlWithHttps(completionUrl);

        const result = await createSnapshots.mutateAsync({
          params: [
            {
              name: `${completionUrl} - Desktop`,
              siteUrl: normalizedHeatmapUrl,
              device: DEVICE_TYPES.DESKTOP,
              trackingDevices: [DEVICE_TYPES.DESKTOP],
            },
            {
              name: `${completionUrl} - Tablet`,
              siteUrl: normalizedHeatmapUrl,
              device: DEVICE_TYPES.TABLET,
              trackingDevices: [DEVICE_TYPES.TABLET],
            },
            {
              name: `${completionUrl} - Mobile`,
              siteUrl: normalizedHeatmapUrl,
              device: DEVICE_TYPES.PHONE,
              trackingDevices: [DEVICE_TYPES.PHONE],
            },
          ],
          settings: {
            startsAtOption: SNAPSHOT_START_OPTIONS.IMMEDIATELY,
            expiresAtOption: SNAPSHOT_END_OPTIONS.DEFAULT,
            maxVisits: DEFAULT_VISITS_VALUE,
            useSamplingRatio: false,
            samplingRatio: 20,
            removeElementsOption: SNAPSHOT_BLOCKING_POPUP_OPTIONS.REMOVE_POPUPS,
            useWaitAfterLoad: false,
            waitAfterLoad: 31,
            trackingOption: SNAPSHOT_TRACKING_OPTIONS.DEFAULT,
            reoccurring: false,
          },
        });

        if (result?.createSnapshots?.errors?.snapshots?.length || result?.createSnapshots?.errors?.settings?.length) {
          if (!isSkipMode) {
            notifications.error({ content: 'Heatmap creation failed.', timeout: 3000, skipHoneybadger: true });
          }

          if (isSkipMode) {
            createdHeatmapsCount = result?.createSnapshots?.snapshots?.length ?? 0;
          }

          if (!isSkipMode) {
            return;
          }
        }

        createdHeatmapsCount = result?.createSnapshots?.snapshots?.length ?? 0;
      } else {
        try {
          await createSite.mutateAsync({ url: completionUrl });
        } catch (error) {
          const errorMessage = error?.graphQLErrors?.[0]?.message || 'Site creation failed.';
          const isDuplicateSiteError = errorMessage.toLowerCase().includes('already exists');

          if (!isSkipMode && !isDuplicateSiteError) {
            throw error;
          }
        }
      }
    }

    const flowDestination = flow ? getFlowDestination(flow) : '/';

    const destination =
      isHeatmapFlow && createdHeatmapsCount > 0
        ? withQueryParams(flowDestination, '', { heatmaps_created: createdHeatmapsCount })
        : flowDestination;

    const onboardingUpdates = {};

    if (!completedAt) {
      onboardingUpdates.completedAt = Math.floor(Date.now() / 1000);
    }

    if (completionUrl) {
      onboardingUpdates.siteUrl = completionUrl;
    }

    if (Object.keys(onboardingUpdates).length > 0) {
      try {
        await upsertUserOnboarding(onboardingUpdates);
      } catch {
        // noop: keep completion non-blocking
      }
    }

    navigate(destination, { replace: true });

    if (queryClient) {
      void queryClient.invalidateQueries({ ...globalFeatureKeys.me });
      void queryClient.refetchQueries({ ...globalFeatureKeys.me });
    }
  } catch {
    if (!isSkipMode) {
      notifications.error({
        content: 'We could not finish setup. Please try again.',
        timeout: 3000,
        skipHoneybadger: true,
      });
    }
  }
}
